import { useQuery } from "@tanstack/react-query";
import type { PropsWithChildren } from "react";
import { useSearchParams } from "react-router-dom";
import { getReturnedGrnAccess } from "../../api/grns";
import { useAuth } from "../../hooks/useAuth";
import { useAttentionRegisterAuthority } from "../../hooks/useAttentionRegisterAuthority";
import { useOfflineStatus } from "../../pwa/hooks/useOfflineStatus";
import { FORM_IDS } from "../../lib/permissionCatalog";
import { RequirePermission } from "./RequirePermission";

export function GRNRegisterPermission({children}: PropsWithChildren) {
  const [params] = useSearchParams();
  return params.get("correction_state")?.trim().toUpperCase() === "RETURNED"
    ? <ReturnedGRNQueuePermission>{children}</ReturnedGRNQueuePermission>
    : <RequirePermission forms={[FORM_IDS.GRN]} attentionRegister>{children}</RequirePermission>;
}

export function GRNDetailPermission({children}: PropsWithChildren) {
  const [params] = useSearchParams();
  const returned = params.get("correction_state")?.trim().toUpperCase() === "RETURNED";
  if (returned && !params.get("vessel_id")?.trim()) return <p role="alert">A valid vessel selection is required for this GRN.</p>;
  return returned
    ? <ReturnedGRNQueuePermission>{children}</ReturnedGRNQueuePermission>
    : <RequirePermission forms={[FORM_IDS.GRN, FORM_IDS.TRACKER]} attentionRegister>{children}</RequirePermission>;
}

export function ReturnedGRNQueuePermission({children}: PropsWithChildren) {
  const {isAuthenticated, isInitialized, isLoading} = useAuth();
  const authority = useAttentionRegisterAuthority();
  const {isOnline} = useOfflineStatus();
  const [params] = useSearchParams();
  const vessel = params.get("vessel_id") ?? undefined;
  const query = useQuery({
    queryKey: ["returned-grn-access", authority, vessel ?? "all-vessels"],
    queryFn: ({signal}) => getReturnedGrnAccess(vessel, signal),
    enabled: isAuthenticated && isInitialized && !isLoading && isOnline,
    retry: false, staleTime: 0, refetchOnMount: "always",
  });
  if (!isOnline) return <p role="status">Connect to load GRNs returned for correction.</p>;
  if (!isInitialized || isLoading || query.isPending || query.isFetching) return <p role="status">Checking returned GRN access...</p>;
  if (query.isError) return <div role="alert">Returned GRN access is unavailable. <button type="button" onClick={() => void query.refetch()}>Retry</button></div>;
  if (!isAuthenticated || !query.data?.vessel_ids.length) return <p role="alert">You do not have permission to view this returned GRN queue.</p>;
  return <>{children}</>;
}
